// src/pages/dashboard/section-labita/SectionLabita.jsx
import React, { useState } from "react";
import { Paper, Stack, Typography, Box, Grid, Card, CardContent, useTheme, useMediaQuery } from "@mui/material";
import AgricultureOutlinedIcon from "@mui/icons-material/AgricultureOutlined";
import PineTreeIcon from "@mui/icons-material/Nature";
import BananaIcon from "@mui/icons-material/Spa";

import TotalStokLahanNF from "../../labita/total-stok-lahan/TotalStokLahanNF";

function SectionLabita({ paperShadow }) {
    const theme = useTheme(); // Pindahkan ke sini, di level atas komponen
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // State untuk menyimpan total stok lahan bongkar
    const [stokLahanBongkar, setStokLahanBongkar] = useState({
        freshPine: 0,
        banana: 0,
    });

    // Fungsi untuk menangani perubahan data total stok lahan bongkar
    const handleTotalStokLahanBongkarChange = (data) => {
        setStokLahanBongkar({
            freshPine: data.freshPine,
            banana: data.banana,
        });
    };

    // Fungsi untuk memformat angka dengan pemisah ribuan
    const formatNumberWithSeparator = (number) => {
        if (number === 0) return "0";
        return number.toLocaleString("id-ID", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        });
    };

    return (
        <Paper
            sx={{
                p: 3,
                mb: 3,
                borderRadius: 2,
                boxShadow: paperShadow,
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 0 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <AgricultureOutlinedIcon sx={{ fontSize: 32, color: "#795548" }} />
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            Labita
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Deskripsi
                        </Typography>
                    </Box>
                </Stack>

                {/* Right Side - Summary Cards */}
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems="center"
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                    {/* Stok Lahan Bongkar Fresh Pine Card */}
                    <Card
                        elevation={0}
                        sx={{
                            width: { xs: "100%", sm: 180 },
                            height: { xs: "auto", sm: 72 },
                            border: `1px solid ${theme.palette.divider}`, // theme sudah didefinisikan
                            borderRadius: 2,
                            backgroundColor: "#e8f5e9",
                            transition: "transform 0.2s, box-shadow 0.2s",
                            "&:hover": {
                                transform: "translateY(-4px)",
                                boxShadow: theme.shadows[4],
                                borderColor: "#4caf50",
                            },
                        }}
                    >
                        <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                            <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 0 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: 32,
                                        height: 32,
                                        borderRadius: "50%",
                                        backgroundColor: "primary.50",
                                        color: "#4caf50",
                                    }}
                                >
                                    <PineTreeIcon sx={{ fontSize: 18 }} />
                                </Box>
                                <Box>
                                    <Typography
                                        variant="h5"
                                        fontWeight="bold"
                                        color="#4caf50"
                                        sx={{
                                            fontSize: { xs: "1.5rem", sm: "18px" },
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {formatNumberWithSeparator(stokLahanBongkar.freshPine)} Ha
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: "12px",
                                        }}
                                    >
                                        Stok Lahan Bongkar<br/>Fresh Pine
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>

                    {/* Stok Lahan Bongkar Banana Card */}
                    <Card
                        elevation={0}
                        sx={{
                            width: { xs: "100%", sm: 180 },
                            height: { xs: "auto", sm: 72 },
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            backgroundColor: "#fffde7",
                            transition: "transform 0.2s, box-shadow 0.2s",
                            "&:hover": {
                                transform: "translateY(-4px)",
                                boxShadow: theme.shadows[4],
                                borderColor: "#fbc02d",
                            },
                        }}
                    >
                        <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                            <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 0 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: 32,
                                        height: 32,
                                        borderRadius: "50%",
                                        backgroundColor: "secondary.50",
                                        color: "#fbc02d",
                                    }}
                                >
                                    <BananaIcon sx={{ fontSize: 18 }} />
                                </Box>
                                <Box>
                                    <Typography
                                        variant="h6"
                                        fontWeight="bold"
                                        color="#fbc02d"
                                        sx={{
                                            fontSize: { xs: "1.5rem", sm: "18px" },
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {formatNumberWithSeparator(stokLahanBongkar.banana)} Ha
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: "12px",
                                        }}
                                    >
                                        Stok Lahan Bongkar<br/>Banana
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Stack>
            </Stack>

            <Typography variant="body1" color="text.primary">
                <Grid container spacing={12}>
                    <Grid size={12}>
                        {/* Kirim callback untuk mendapatkan data total stok lahan bongkar */}
                        <TotalStokLahanNF onTotalStokLahanBongkarChange={handleTotalStokLahanBongkarChange} />
                    </Grid>
                </Grid>
            </Typography>
        </Paper>
    );
}

export default SectionLabita;
